// quiz-phases.tsx — phase render components extracted from quiz-view.tsx.
//
// Session 50: View monolith decomposition — DEBT-19BI-VIEW-MONOLITH.
// Extracts: QuizSetupPhase, QuizRunningPhase, QuizFinishedPhase,
// TfQuestionCard, McqQuestionCard.
// quiz-view.tsx 447→~130 lines.

import { useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { Bookmark, Trophy, RotateCcw } from 'lucide-react';
import type { MCQ, TrueFalse, CourseProgress, IngestPackage, DiagnoseVerdict } from '@examforge/shared/schemas';
import { resolvedField, overrideKey } from '@examforge/shared/schemas';
import type { TFunction } from '../i18n/use-t.js';
import { updateProgress } from '../storage/idb.js';
import { EditableText } from '../components/editable-text.js';
import { EditableField } from '../components/editable-field.js';
import { ItemActions } from '../components/item-actions.js';
import { MCQCard } from '../components/cards/index.js';
import { isCorrectAnswer } from '../lib/quiz-utils.js';
import { useUnifiedRegen } from '../lib/hooks/use-unified-regen.js';
import type { RegenAction } from '@examforge/shared/schemas';
import { PRESET_COUNTS } from '../lib/hooks/use-quiz-session.js';
import type { QuizItem, QuizKind, SetupConfig, AttemptRecord } from '../lib/hooks/use-quiz-session.js';
import type { ActiveQuizSession } from '@examforge/shared/schemas';

// ── Shared prop types ──────────────────────────────────────────────────────────

export interface SetupPhaseProps {
  t: TFunction;
  kindParam: QuizKind;
  pkg: IngestPackage;
  config: SetupConfig;
  setConfig: (updater: (c: SetupConfig) => SetupConfig) => void;
  persistedSession: ActiveQuizSession | null;
  onResume: () => void;
  onAbandon: () => void;
  onStart: () => void;
}

export interface RunningPhaseProps {
  t: TFunction;
  resolvedItem: QuizItem;
  currentIdx: number;
  totalQuestions: number;
  picked: number | null;
  isBookmarked: boolean;
  progress: CourseProgress;
  pkg: IngestPackage;
  draft: { id: string };
  refreshProgress: () => Promise<void>;
  onPickOption: (i: number) => void;
  onNextOrFinish: () => void;
  onToggleBookmark: () => void;
}

export interface FinishedPhaseProps {
  t: TFunction;
  navigate: NavigateFunction;
  attempts: readonly AttemptRecord[];
  onRestart: () => void;
  /** B5 桥5: 课程 id，用于触发针对性补练 regen。 */
  courseId?: string;
  /** B5 桥5: 已 accept 的诊断 verdict，有值时完成态显示补练推荐块。 */
  diagnosisVerdict?: DiagnoseVerdict | null;
}

// ── QuizSetupPhase ────────────────────────────────────────────────────────────

export function QuizSetupPhase({
  t, kindParam, pkg, config, setConfig,
  persistedSession, onResume, onAbandon, onStart,
}: SetupPhaseProps) {
  return (
    <section className="quiz-setup">
      {persistedSession && persistedSession.questionIds.length > 0 && (
        <div className="quiz-resume">
          <h2>{t('quiz.resumeHeading')}</h2>
          <p>
            {t('quiz.resumeBody', { current: persistedSession.currentIdx + 1, total: persistedSession.questionIds.length })}
          </p>
          <div className="quiz-resume-buttons">
            <button className="btn-primary" onClick={onResume}>
              {t('quiz.resumeContinue')}
            </button>
            <button className="btn-secondary" onClick={onAbandon}>
              {t('quiz.resumeAbandon')}
            </button>
          </div>
        </div>
      )}

      <h2>{kindParam === 'tf' ? (t('dashboard.itemKindTf') + ' — ' + t('quiz.setupHeading')) : t('quiz.setupHeading')}</h2>
      <label className="field">
        <span className="field-label">{t('quiz.pickCase')}</span>
        <select
          className="field-input"
          value={config.caseId}
          onChange={(e) => setConfig((c) => ({ ...c, caseId: e.target.value as typeof config['caseId'] }))}
        >
          <option value="all">{t('flashcards.allCases')}</option>
          {pkg.cases.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field-label">{t('quiz.pickCount')}</span>
        <div className="chip-grid">
          {PRESET_COUNTS.map((n) => (
            <label key={String(n)} className={`chip${config.count === n ? ' is-active' : ''}`}>
              <input
                type="radio"
                name="quiz-count"
                checked={config.count === n}
                onChange={() => setConfig((c) => ({ ...c, count: n }))}
              />
              <span>{n === 'all' ? t('flashcards.allCases') : n}</span>
            </label>
          ))}
        </div>
      </label>
      <div className="quiz-setup-buttons">
        <button className="btn-primary btn-primary-lg" onClick={onStart}>
          {t('quiz.start')}
        </button>
      </div>
    </section>
  );
}

// ── QuizRunningPhase ──────────────────────────────────────────────────────────

export function QuizRunningPhase({
  t, resolvedItem, currentIdx, totalQuestions, picked,
  isBookmarked, progress, pkg, draft, refreshProgress,
  onPickOption, onNextOrFinish, onToggleBookmark,
}: RunningPhaseProps) {
  return (
    <section className="quiz-running">
      <header className="quiz-running-head">
        <span className="quiz-counter">
          {t('quiz.questionCounter', { current: currentIdx + 1, total: totalQuestions })}
        </span>
        <button
          type="button"
          className={`bookmark-btn${isBookmarked ? ' is-active' : ''}`}
          onClick={onToggleBookmark}
          title={isBookmarked ? t('bookmarks.removeBookmark') : t('bookmarks.addBookmark')}
        >
          <Bookmark size={14} fill={isBookmarked ? 'currentColor' : 'none'} aria-hidden="true" />
        </button>
        {picked !== null && (() => {
          const correct = isCorrectAnswer(resolvedItem, picked);
          return (
            <span className={`quiz-feedback-pill ${correct ? 'is-correct' : 'is-wrong'}`}>
              {correct ? t('quiz.correct') : t('quiz.incorrect')}
            </span>
          );
        })()}
      </header>

      {resolvedItem.kind === 'tf' ? (
        <TfQuestionCard
          item={resolvedItem.data}
          picked={picked}
          onPick={onPickOption}
          courseId={draft.id}
          refreshProgress={refreshProgress}
        />
      ) : (
        <McqQuestionCard
          item={resolvedItem.data}
          picked={picked}
          onPick={onPickOption}
          progress={progress}
          pkg={pkg}
          draft={draft}
          refreshProgress={refreshProgress}
          t={t}
        />
      )}

      {/* §2.2 visibility gate: explanation hidden until user picks an answer. */}
      {picked !== null && (() => {
        const data = resolvedItem.data;
        const explanation = 'explanation' in data ? (data as { explanation?: string }).explanation : undefined;
        if (!explanation) return null;
        const resolved = resolvedField(
          progress.itemOverrides,
          data.id,
          'explanation',
          explanation,
        );
        const commitOverride = async (newText: string): Promise<void> => {
          await updateProgress(draft.id, (p) => {
            if (!p.itemOverrides) p.itemOverrides = {};
            p.itemOverrides[overrideKey(data.id, 'explanation')] = {
              itemKind: resolvedItem.kind,
              field: 'explanation',
              editedText: newText,
              editedAt: new Date().toISOString(),
              originalSnapshot: resolved.original,
            };
          });
          await refreshProgress();
        };
        const revertOverride = async (): Promise<void> => {
          await updateProgress(draft.id, (p) => {
            if (p.itemOverrides) {
              delete p.itemOverrides[overrideKey(data.id, 'explanation')];
            }
          });
          await refreshProgress();
        };
        return (
          <div className="quiz-explanation markdown-body">
            <div className="quiz-explanation-head">
              <h3>{t('quiz.explanation')}</h3>
              <div className="quiz-explanation-meta">
                {'cognitiveLevel' in data && data.cognitiveLevel && (
                  <span className={`quiz-meta-chip cog-${data.cognitiveLevel}`}>
                    {data.cognitiveLevel}
                  </span>
                )}
                {'category' in data && data.category && (
                  <span className={`quiz-meta-chip cat-${data.category}`}>
                    {data.category}
                  </span>
                )}
                {'difficulty' in data && data.difficulty && (
                  <span className={`quiz-meta-chip diff-${data.difficulty}`}>
                    {data.difficulty}
                  </span>
                )}
              </div>
            </div>
            <EditableText
              text={resolved.text}
              isEdited={resolved.isEdited}
              onCommit={commitOverride}
              onRevert={revertOverride}
            />
            <button className="btn-primary" onClick={onNextOrFinish} style={{ marginTop: '0.75rem' }}>
              {currentIdx + 1 >= totalQuestions ? t('quiz.finishedHeading') : t('quiz.nextQuestion')}
            </button>
          </div>
        );
      })()}

      <p className="quiz-shortcuts">
        {resolvedItem.kind === 'tf' ? t('quiz.tfShortcuts') : t('quiz.shortcuts')}
      </p>
    </section>
  );
}

// ── QuizFinishedPhase ─────────────────────────────────────────────────────────

export function QuizFinishedPhase({
  t, navigate, attempts, onRestart, courseId, diagnosisVerdict,
}: FinishedPhaseProps) {
  const total = attempts.length;
  const correctCount = attempts.filter((a) => a.correct).length;
  const percent = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  return (
    <section className="quiz-finished">
      <Trophy size={48} aria-hidden="true" className="quiz-finished-icon" />
      <h2>{t('quiz.finishedHeading')}</h2>
      <p className="quiz-finished-score">
        {t('quiz.finishedScore', { correct: correctCount, total, percent })}
      </p>
      {/* B5 桥5: 有已 accept 的诊断时，完成态展示针对性补练推荐块。
          铁律 3 守护：块本身不自动调 LLM，必须用户点「生成练习题」才触发 regen。 */}
      {courseId && diagnosisVerdict && (
        <QuizRecommendPractice
          t={t}
          courseId={courseId}
          verdict={diagnosisVerdict}
        />
      )}
      <div className="quiz-finished-buttons">
        <button className="btn-primary" onClick={onRestart}>
          <RotateCcw size={14} aria-hidden="true" />
          {t('quiz.finishedRestart')}
        </button>
        <button className="btn-secondary" onClick={() => navigate('../wrong-answers')}>
          {t('quiz.finishedReviewWrong')}
        </button>
        <button className="btn-secondary" onClick={() => navigate('..')}>
          {t('quiz.finishedBackToHub')}
        </button>
      </div>
    </section>
  );
}

// ── QuizRecommendPractice (B5 桥5) ────────────────────────────────────────────
//
// quiz 完成态的针对性补练推荐块。仅当用户已有 AI 诊断 verdict 且 verdict
// 含 chapter 维度弱点时渲染。
//
// 推荐逻辑：verdict.weaknesses 里有 attribution==='chapter' → 说明诊断识别出
// 章节级弱点，推荐生成针对性 MCQ 补练。生成范围不限定 caseIds（避免引入 pkg
// 反查依赖），改由 userPromptHint 携带弱点证据（target + evidence）传给 LLM，
// 让 LLM 基于整个课程生成针对性题。精准章节匹配是后续可演进项（传入 pkg/caseById）。
//
// 铁律 3 守护：块本身不触发任何 LLM 调用。「生成练习题」按钮点击才调
// useUnifiedRegen.trigger（add 模式 · mcqs · count 5 · forceDeep 因章节级超 30s）。

export function QuizRecommendPractice({
  t,
  courseId,
  verdict,
}: {
  t: TFunction;
  courseId: string;
  verdict: DiagnoseVerdict;
}) {
  const hasChapterWeakness = verdict.weaknesses.some((w) => w.attribution === 'chapter');

  // Hooks MUST be called unconditionally (before any early return) per the
  // Rules of Hooks. We always set up the regen hook + state, then bail out
  // of rendering after.
  const regen = useUnifiedRegen(courseId);
  const [generating, setGenerating] = useState(false);

  if (!hasChapterWeakness) return null;

  const handleGenerate = async (): Promise<void> => {
    if (generating) return;
    setGenerating(true);
    const evidenceHint = verdict.weaknesses
      .filter((w) => w.attribution === 'chapter')
      .map((w) => `${w.target}: ${w.evidence}`)
      .join('; ');
    const action: RegenAction = {
      kind: 'add',
      scope: { stage: 'mcqs' },
      count: 5,
      userPromptHint: `针对诊断弱点的补练：${evidenceHint}`,
    };
    await regen.trigger(action, 'mcqs', undefined, { forceDeep: true });
    setGenerating(false);
  };

  return (
    <div className="quiz-recommend-practice" data-testid="quiz-recommend-practice">
      <h3 className="quiz-recommend-title">{t('quiz.recommendPractice')}</h3>
      <p className="quiz-recommend-hint">{t('quiz.recommendPracticeHint')}</p>
      <button
        type="button"
        className="btn-secondary"
        disabled={generating}
        onClick={() => { void handleGenerate(); }}
      >
        {generating ? t('common.loading') : t('quiz.recommendGenerate')}
      </button>
    </div>
  );
}

// ── TfQuestionCard ────────────────────────────────────────────────────────────

export function TfQuestionCard({
  item,
  picked,
  onPick,
  courseId,
  refreshProgress,
}: {
  item: TrueFalse;
  picked: number | null;
  onPick: (i: number) => void;
  courseId?: string;
  refreshProgress?: () => Promise<void>;
}) {
  const isAnswered = picked !== null;
  const userPickedTrue = picked === 0;
  const userPickedFalse = picked === 1;
  const correctIsTrue = item.correct === true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const it = item as Record<string, any>;

  return (
    <div className="tf-question-card">
      <div className="tf-statement markdown-body">
        <p>{item.statement}</p>
      </div>
      <div className="tf-buttons">
        <button
          type="button"
          className={`tf-btn tf-btn--true${userPickedTrue ? ' is-selected' : ''}${isAnswered && userPickedTrue ? (correctIsTrue ? ' is-correct' : ' is-wrong') : ''}${isAnswered && !userPickedTrue && correctIsTrue ? ' is-correct-hint' : ''}`}
          disabled={isAnswered}
          onClick={() => onPick(0)}
        >
          <span className="tf-btn-key">T</span>
          <span>True</span>
        </button>
        <button
          type="button"
          className={`tf-btn tf-btn--false${userPickedFalse ? ' is-selected' : ''}${isAnswered && userPickedFalse ? (!correctIsTrue ? ' is-correct' : ' is-wrong') : ''}${isAnswered && !userPickedFalse && !correctIsTrue ? ' is-correct-hint' : ''}`}
          disabled={isAnswered}
          onClick={() => onPick(1)}
        >
          <span className="tf-btn-key">F</span>
          <span>False</span>
        </button>
      </div>
      {isAnswered && refreshProgress && courseId && (
        <div className="tf-item-actions">
          <ItemActions
            courseId={courseId}
            item={{
              id: item.id,
              caseId: it['caseId'] as string | undefined,
              caseIds: it['caseIds'] as string[] | undefined,
              conceptIds: it['conceptIds'] as string[] | undefined,
            }}
            stage="true_false"
            onRegenComplete={() => { void refreshProgress(); }}
            flagKind="tf"
            flagItemJson={item}
          />
        </div>
      )}
    </div>
  );
}

// ── McqQuestionCard ───────────────────────────────────────────────────────────

export function McqQuestionCard({
  item: currentMcq,
  picked,
  onPick,
  progress,
  pkg,
  draft,
  refreshProgress,
  t,
}: {
  item: MCQ;
  picked: number | null;
  onPick: (i: number) => void;
  progress: CourseProgress;
  pkg: IngestPackage;
  draft: { id: string };
  refreshProgress: () => Promise<void>;
  t: TFunction;
}) {
  return (
    <MCQCard
      item={{
        ...currentMcq,
        options: currentMcq.options.map((opt, i) =>
          resolvedField(progress.itemOverrides, currentMcq.id, `option.${i}`, opt).text,
        ) as MCQ['options'],
      }}
      mode={picked === null ? 'play' : 'review'}
      selectedIndex={picked}
      onSelect={onPick}
      className="mcq-card--quiz-host"
      questionSlot={
        <EditableField
          itemId={currentMcq.id}
          itemKind="mcq"
          field="question"
          aiOriginal={currentMcq.question}
        />
      }
      slot={
        picked !== null ? (
          <details className="quiz-options-editor">
            <summary>{t('quiz.editOptionsFlag')}</summary>
            <ol className="quiz-options-editor-list">
              {currentMcq.options.map((opt, i) => (
                <li key={i}>
                  <span className="quiz-option-key">{String.fromCharCode(65 + i)}</span>
                  <EditableField
                    itemId={currentMcq.id}
                    itemKind="mcq"
                    field={`option.${i}`}
                    aiOriginal={opt}
                  />
                </li>
              ))}
              <li className="quiz-options-editor-question-flag">
                <ItemActions
                  courseId={draft.id}
                  item={{
                    id: currentMcq.id,
                    caseId: currentMcq.caseId ?? undefined,
                    caseIds: (currentMcq as Record<string, unknown>)['caseIds'] as string[] | undefined,
                    conceptIds: (currentMcq as Record<string, unknown>)['conceptIds'] as string[] | undefined,
                  }}
                  stage="mcqs"
                  onRegenComplete={() => { void refreshProgress(); }}
                  flagKind="mcq"
                  flagItemJson={currentMcq}
                  flagContextItems={pkg.mcqs
                    .filter((m) => m.id !== currentMcq.id && m.caseId === currentMcq.caseId)
                    .slice(0, 3)}
                />
                <span style={{ fontSize: '0.78rem', opacity: 0.7 }}>
                  {t('quiz.wholeItemFlag')}
                </span>
              </li>
            </ol>
          </details>
        ) : null
      }
    />
  );
}
